import React, { useMemo } from 'react';
import { Box } from '@common-fe/common-fe';

import { OrganizationTypes } from '@/common/constants';
import { BankingBalanceDetailItem } from '@/modules/employer/components/BankingBalance/types';

import BankingBalanceDetailRow from './BankingBalanceDetailRow';

const OPTIONAL_FIELD_TITLES: Record<string, string> = {
  employerCalculatedPrefund: 'Calculated prefund',
  employerPrefundHolding: 'Prefund holding',
  employerReplenishmentToBeInvoiced: 'Replenishment to be invoiced',
  employerReplenishmentNotReceived: 'Replenishment not received',
  employeeContributionsTotalReceived: 'Contribution total received',
  employeeContributionsNotReceivedYet: 'Contributions not received',
};

const OPTIONAL_FIELD_DESCRIPTION: Record<string, string> = {
  employerReplenishmentToBeInvoiced: 'Current claim invoice accrual',
  employerReplenishmentNotReceived: 'Claim invoice requested but funds not received',
  employeeContributionsNotReceivedYet: 'Contribution invoice requested but funds not received',
};

const EMPLOYER_CALCULATED_PREFUND_KEY = 'employerCalculatedPrefund';

type OptionalFieldKey = keyof typeof OPTIONAL_FIELD_TITLES;

interface Props {
  data?: BankingBalanceDetailItem;
}

const BankingBalanceOrgDetailsOptionalFields: React.FC<Props> = ({ data }) => {
  const visibleFields = useMemo(() => {
    if (!data) return [];

    return (Object.keys(OPTIONAL_FIELD_TITLES) as OptionalFieldKey[])
      .filter((key) => {
        if (data[key as keyof BankingBalanceDetailItem] == null) return false;

        if (key === EMPLOYER_CALCULATED_PREFUND_KEY) {
          return data.orgType === OrganizationTypes.company
            || data.orgType === OrganizationTypes.subsidiary;
        }

        return true;
      })
      .map((key) => ({
        key,
        label: OPTIONAL_FIELD_TITLES[key],
        value: data[key as keyof BankingBalanceDetailItem] as number,
        description: OPTIONAL_FIELD_DESCRIPTION[key],
      }));
  }, [data]);

  if (!visibleFields.length) return null;

  return (
    <Box gap="spacing4">
      {visibleFields.map((field) => (
        <BankingBalanceDetailRow
          key={field.key}
          testId={`org-details_${field.key}`}
          label={field.label}
          value={field.value}
          description={field?.description}
        />
      ))}
    </Box>
  );
};

export default BankingBalanceOrgDetailsOptionalFields;
